import React from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { useApiClient, getLocation, updateLocation, LocationDetailsDto } from '../api/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../toast/ToastProvider';

export const LocationEditPage: React.FC = () => {
  const { t } = useI18n();
  const { request } = useApiClient();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [location, setLocation] = React.useState<LocationDetailsDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const [privateName, setPrivateName] = React.useState('');
  const [publicName, setPublicName] = React.useState('');
  const [isPrivate, setIsPrivate] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLocation(request, id);
        setLocation(data);
        setPrivateName(data.privateName || '');
        setPublicName(data.publicName);
        setIsPrivate(data.isPrivate);
      } catch (e: any) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, request]);

  const handleSave = async () => {
    if (!id || !location) return;
    setSaving(true);
    try {
      await updateLocation(request, id, {
        privateName: privateName.trim() || null,
        publicName: publicName.trim(),
        isPrivate,
      });
      showSuccess(t('locations.updateSuccess'));
      navigate('/locations');
    } catch (e: any) {
      showError(`${t('common.error')}: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <button
          onClick={() => navigate('/locations')}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          ← {t('action.back')}
        </button>
      </div>

      <h1 className="text-2xl font-extrabold mb-4">{t('locations.editTitle')}</h1>

      {loading && <div className="text-sm text-gray-600">{t('common.loading')}</div>}
      {error && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-900 border border-red-200">
          {t('common.error')}: {error}
        </div>
      )}

      {!loading && !error && location && (
        <div className="rounded border bg-white p-6 max-w-2xl">
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-semibold mb-1">
                {t('locations.isPrivate')}
              </label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={!isPrivate}
                    onChange={() => setIsPrivate(false)}
                  />
                  <span className="text-sm">{t('locations.public')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={isPrivate}
                    onChange={() => setIsPrivate(true)}
                  />
                  <span className="text-sm">{t('locations.private')}</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('locations.privateDescription')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                {t('locations.publicName')}
              </label>
              <input
                type="text"
                value={publicName}
                onChange={(e) => setPublicName(e.target.value)}
                className="w-full rounded border px-3 py-2"
                placeholder={t('locations.publicNamePlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('locations.publicNameDescription')}
              </p>
            </div>

            {isPrivate && (
              <div>
                <label className="block text-sm font-semibold mb-1">
                  {t('locations.privateName')}
                </label>
                <input
                  type="text"
                  value={privateName}
                  onChange={(e) => setPrivateName(e.target.value)}
                  className="w-full rounded border px-3 py-2"
                  placeholder={t('locations.privateNamePlaceholder')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('locations.privateNameDescription')}
                </p>
              </div>
            )}

            <div className="border-t pt-4">
              <div className="text-sm font-semibold mb-1">{t('locations.coordinates')}</div>
              <div className="text-sm text-gray-600">
                {t('locations.longitude')}: {location.longitude.toFixed(6)}
              </div>
              <div className="text-sm text-gray-600">
                {t('locations.latitude')}: {location.latitude.toFixed(6)}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={handleSave}
                disabled={saving || !publicName.trim()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-extrabold text-black hover:brightness-95 disabled:opacity-50"
              >
                {saving ? t('common.saving') : t('action.save')}
              </button>
              <button
                onClick={() => navigate('/locations')}
                disabled={saving}
                className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                {t('action.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationEditPage;

